import { exists } from '@jack-henry/frontend-utils/functions';
import { CheckExceptionModelDto } from '@treasury/api/channel';
import { DomainEntity } from '../../../../shared';

const defaultCheckExceptionModelDto: CheckExceptionModelDto = {
    arpExceptionDetailUniqueId: '',
    arpExceptionDetailId: 0,
    paidAmount: 0,
    companyId: 0,
    isCorrectionRequestCreated: false,
    returnReasonDescription: undefined,
};
export class CheckException extends DomainEntity<CheckExceptionModelDto> {
    public createDefault(): this {
        return new CheckException(defaultCheckExceptionModelDto) as this;
    }

    public get arpExceptionDetailUniqueId() {
        return this.dto.arpExceptionDetailUniqueId;
    }

    public get arpExceptionDetailId() {
        return this.dto.arpExceptionDetailId;
    }

    public get paidAmount() {
        return this.dto.paidAmount;
    }

    public get companyId() {
        return this.dto.companyId;
    }

    public get isCorrectionRequestCreated() {
        return this.dto.isCorrectionRequestCreated;
    }

    public get accountNumber() {
        return this.dto.accountNumber;
    }

    public get checkNumber() {
        return this.dto.checkNumber;
    }

    public get issuedAmount() {
        return this.dto.issuedAmount;
    }

    public get postedDate() {
        return this.dto.postedDate;
    }

    public get issuedDate() {
        return this.dto.issuedDate;
    }

    public get issuedPayee() {
        return this.dto.issuedPayee;
    }

    public get decisionChoice() {
        return this.dto.decisionStatus;
    }

    public set decisionChoice(value: string | undefined) {
        this.dto.decisionStatus = value;
    }

    public get initialDecision() {
        return this.dto.decisionStatus;
    }

    public set initialDecision(value: string | undefined) {
        this.dto.decisionStatus = value;
    }

    public get decisionStatus() {
        return this.dto.decisionStatus;
    }

    public get comment() {
        return this.dto.comment || '';
    }

    public set comment(value: string) {
        this.dto.comment = value;
    }

    public get exceptionReason() {
        return this.dto.exceptionReason;
    }

    public get financialInstitutionId() {
        return this.dto.financialInstitutionId;
    }

    public get companyName() {
        return this.dto.companyName;
    }

    public get companyUniqueId() {
        return this.dto.companyUniqueId;
    }

    public get fromWorkStation() {
        return this.dto.fromWorkStation;
    }

    public get attachmentFileName() {
        return this.dto.attachmentFileName;
    }

    public set attachmentFileName(value: string | undefined) {
        this.dto.attachmentFileName = value;
    }

    /**
     * NOTE: The value for return reason maps to the unique id
     * This allows us to save the return reason
     */
    public get returnReason() {
        return this.dto.returnReasonUniqueId || '';
    }

    /**
     * NOTE: The value for return reason maps from the unique id
     * This allows us to display persisted return reasons
     */
    public set returnReason(value: string) {
        this.dto.returnReasonUniqueId = value;
    }

    public get returnReasonUniqueId() {
        return this.dto.returnReasonUniqueId;
    }

    public get returnReasonId() {
        return this.dto.returnReasonId;
    }

    public get lastDecisionByUserUniqueId() {
        return this.dto.lastDecisionByUserUniqueId;
    }

    public get updatedBy() {
        return this.dto.updatedBy;
    }

    public get sourceOfEntry() {
        return this.dto.sourceOfEntry;
    }

    public get isProtected() {
        return this.dto.protected === 'Y';
    }

    public get protected() {
        return this.dto.protected;
    }

    public get ddaBatchNumber() {
        return this.dto.ddaBatchNumber;
    }

    public get ddaSequenceNumber() {
        return this.dto.ddaSequenceNumber;
    }

    public get posPayItemId() {
        return this.dto.posPayItemId;
    }

    public get returnReasonDescription() {
        if (!this.dto.returnReasonDescription) {
            return undefined;
        }
        return this.dto.returnReasonDescription;
    }

    public get lastDecisionTakenBy() {
        return this.dto.lastDecisionTakenBy;
    }

    public get audits() {
        return this.dto.audits;
    }

    public get checkImageNumber() {
        return this.dto.checkImageNumber;
    }

    public get attachment() {
        return this.dto.attachment;
    }

    public get decisionNotFromFi() {
        return this.dto.lastDecisionTakenBy !== 'Financial Institution';
    }

    public get userDecisionedToday() {
        return (
            this.dto.audits?.reduce((prev, audit) => {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                const sinceYesterday = new Date(audit.timestamp ?? 0) >= yesterday;

                const isDecision =
                    audit.action === 'Return' ||
                    audit.action === 'Pay' ||
                    (exists(this.dto.returnReasonId) && this.dto.decisionStatus === 'Return');

                return prev || (this.decisionNotFromFi && sinceYesterday && isDecision);
            }, false) ?? false
        );
    }

    public toSaveDto() {
        return {
            ...this.dto,
            pay: this.decisionChoice === 'Pay',
            existingDecision: this.decisionChoice === this.initialDecision,
            returnReasonUniqueId: this.returnReasonUniqueId,
            returnReasonComment: this.comment,
        };
    }
}
